<script setup lang="ts">
import { ref, toRef } from 'vue';

import VBackButton from '@components/buttons/VBackButton/VBackButton.vue';
import VLoader from '@components/loader/VLoader.vue';
import VMessage from '@components/message/VMessage/VMessage.vue';
import {
  TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS,
} from '@modules/affiliate-program-commissions/constants';
import CommissionCountriesTabs from '@modules/affiliate-program-commissions/submodules/edit/components/CommissionCountriesTabs/CommissionCountriesTabs.vue';
import CommissionDeals from '@modules/affiliate-program-commissions/submodules/edit/components/CommissionDeals/CommissionDeals.vue';
import CommissionSteps from '@modules/affiliate-program-commissions/submodules/edit/components/CommissionSteps/CommissionSteps.vue';
import { useDevice } from '@modules/core/composables/device';
import VFormMultiselectFilterWidget from '@modules/form-widgets/components/VFormMultiselectFilterWidget/VFormMultiselectFilterWidget.vue';
import VFormSelectButtonTaggedErrorWidget from '@modules/form-widgets/components/VFormSelectButtonTaggedErrorWidget/VFormSelectButtonTaggedErrorWidget.vue';
import VForm from '@modules/forms/components/VForm/VForm.vue';

import type { AffiliateProgramCommissionsFormProps } from './types';
import CommissionVersionDescriptionModal from '../CommissionVersionDescriptionModal/CommissionVersionDescriptionModal.vue';
import {
  useCommissionCountriesSelector,
  useCommissionCountryFilter,
  useCommissionCountryFilterWithErrors,
  useCommissionDeals,
  useCommissionFormSchema,
  useCommissionSteps,
  useSaveCommission,
} from './composables';

const props = withDefaults(defineProps<AffiliateProgramCommissionsFormProps>(), {});

const isGreaterThenSm = useDevice().breakpoints.sm;
const isRevShareDialogOpened = ref(false);

const isLoading = toRef(props, 'isLoading');
const commission = toRef(props, 'commission');
const countries = toRef(props, 'countries');
const constants = toRef(props, 'constants');
const merchantsList = toRef(props, 'merchantsList');
const isEdit = toRef(props, 'isEdit');

const {
  formSchema,
  formGroups,
  transactionType,
  onFormChange,
  firstGroupStepTransactionType,
  secondGroupStepTransactionType,
  selectedProduct,
  selectedVersions,
  currentFirstGroupVersion,
  currentSecondGroupVersion,
  productOptions,
} = useCommissionFormSchema(
  commission,
  merchantsList,
  constants,
  isEdit,
  toRef(props, 'isReadonly'),
  toRef(props, 'hideAttributes'),
);

const {
  activeFilterIndex,
  countryFilters,
  onFilterAdd,
  onFilterSelect,
  onFilterExpandToggle,
  onFilterRemove,
} = useCommissionCountryFilter(
  commission,
  transactionType,
  currentSecondGroupVersion,
  secondGroupStepTransactionType,
);

const {
  countryFiltersWithErrors,
  activeCountryFilter,
  totalFilterErrors,
  displayFilterErrors,
  countryTabs,
} = useCommissionCountryFilterWithErrors(
  countryFilters,
  activeFilterIndex,
  firstGroupStepTransactionType,
  secondGroupStepTransactionType,
  transactionType,
  selectedProduct,
  productOptions,
);

const {
  countriesOptions,
  onChangeFilterName,
  onChangeValue,
} = useCommissionCountriesSelector(
  countries,
  activeCountryFilter,
  activeFilterIndex,
  countryFilters,
);

const {
  dealsResultOptions,
  deals,
  dealsComparisonOptions,
  onDealAdd,
  onDealRemove,
  onDealChange,
} = useCommissionDeals(
  activeCountryFilter,
  transactionType,
  countryFilters,
  activeFilterIndex,
  constants,
  currentFirstGroupVersion,
);

const {
  firstGroupSteps,
  firstGroupStepResultOptions,
  firstGroupValueSuffix,
  allSecondGroupSteps,
  secondGroupSteps,
  secondGroupStepResultOptions,
  secondGroupValueSuffix,
  secondGroupPaymentColumnOptions,
  stepComparisonOptions,
  onStepAdd,
  onStepRemove,
  onProductStepsRemove,
  onStepChange,
} = useCommissionSteps(
  activeCountryFilter,
  transactionType,
  countryFilters,
  activeFilterIndex,
  constants,
  firstGroupStepTransactionType,
  secondGroupStepTransactionType,
  selectedProduct,
  currentFirstGroupVersion,
  currentSecondGroupVersion,
);

const {
  onSubmit,
  clearError,
  isLoadingSave,
  errors,
} = useSaveCommission(
  commission,
  countryFiltersWithErrors,
  totalFilterErrors,
  selectedVersions,
  selectedProduct,
  currentSecondGroupVersion,
);

function openRevenueShareVersionsDescription(): void {
  isRevShareDialogOpened.value = true;
}
</script>

<template>
  <VLoader v-data-qa-auto-id="'AffiliateProgramCommissionsForm'"
    :loading="isLoading"
    min-height="400px"
  >
    <VForm
      :schema="formSchema"
      :groups="formGroups"
      :submit-btn-label="$getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'COMMON_BUTTONS_SAVE')"
      :readonly="isReadonly || commission?.read_only"
      :is-loading="isLoadingSave"
      :custom-errors="errors"
      :is-buttons-centered="!isGreaterThenSm"
      :is-group-title-centered="!isGreaterThenSm"
      @change="onFormChange"
      @submit="onSubmit"
      @interacted="clearError"
    >
      <template #front_country_filter_index="{ className, readonly }">
        <CommissionCountriesTabs
          :tabs="countryTabs"
          :class="className"
          :errors="displayFilterErrors"
          :readonly="readonly"
          @add="onFilterAdd"
          @select="onFilterSelect"
          @expand-toggle="onFilterExpandToggle"
          @remove="onFilterRemove"
        />
      </template>
      <template
        #front_country_values="{ className, readonly }"
      >
        <div>
          <VFormMultiselectFilterWidget
            v-if="activeCountryFilter?.expanded"
            :key="activeFilterIndex"
            :options="countriesOptions"
            :filter-label="$getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'FILTER_LABEL_FILTER_NAME_INPUT')"
            :filter-value="activeCountryFilter?.label"
            :class="className"
            :errors="activeCountryFilter?.errors?.validCountriesSelection"
            :readonly="readonly"
            @change-name="onChangeFilterName"
            @change-value="onChangeValue"
          />
        </div>
      </template>
      <template #front_first_group_deals="{ className, readonly }">
        <CommissionDeals
          :comparison-options="dealsComparisonOptions"
          :items="deals"
          :obtained-result-options="dealsResultOptions"
          class="border-b pb-8 last:pb-0"
          :class="className"
          :readonly="readonly"
          :errors="activeCountryFilter?.errors?.validItemAttributes"
          @add="onDealAdd"
          @remove="onDealRemove"
          @change="onDealChange"
        />
      </template>
      <template #front_first_group_steps="{ className, readonly }">
        <CommissionSteps
          :comparison-options="stepComparisonOptions"
          :obtained-result-options="firstGroupStepResultOptions"
          :steps="firstGroupSteps"
          :transaction-type="firstGroupStepTransactionType"
          :step-value-suffix="firstGroupValueSuffix"
          :class="className"
          :errors="activeCountryFilter?.errors?.validFirstGroupSteps"
          :readonly="readonly"
          class="pb-8 last:pb-0"
          @add="onStepAdd"
          @remove="onStepRemove"
          @change="onStepChange"
        />
      </template>
      <template #front_second_group_version_description="{ className }">
        <div :class="className">
          <VMessage
            class="input-max-width"
            severity="info"
          >
            <span
              class="cursor-pointer underline hover:no-underline"
              @click="openRevenueShareVersionsDescription"
            >
              {{ $getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'FORM_OPEN_REVENUE_SHARE_VERSIONS_DESCRIPTION') }}
            </span>
          </VMessage>
        </div>
      </template>
      <template #front_second_group_products="{ className, readonly, onChange, onBlur, onInput, onFocus }">
        <VFormSelectButtonTaggedErrorWidget
          :options="productOptions"
          :value="selectedProduct"
          :class="className"
          :errors="currentSecondGroupVersion === 'v2' ? undefined : activeCountryFilter?.errors?.validSecondGroupProducts"
          :readonly="productOptions.length <= 1"
          :steps="allSecondGroupSteps"
          :hide-delete-button="readonly || currentSecondGroupVersion === 'v2'"
          required
          @change="onChange('front_second_group_products', $event)"
          @blur="onBlur('front_second_group_products', $event)"
          @input="onInput('front_second_group_products', $event)"
          @focus="onFocus('front_second_group_products', $event)"
          @delete="onProductStepsRemove"
        />
      </template>
      <template #front_second_group_steps="{ className, readonly }">
        <CommissionSteps
          :comparison-options="stepComparisonOptions"
          :obtained-result-options="secondGroupStepResultOptions"
          :steps="secondGroupSteps"
          :transaction-type="secondGroupStepTransactionType"
          :product="currentSecondGroupVersion === 'v2' ? undefined : selectedProduct"
          :step-value-suffix="secondGroupValueSuffix"
          :product-payment-column-options="secondGroupPaymentColumnOptions"
          :class="className"
          :errors="activeCountryFilter?.errors?.validSecondGroupSteps"
          :readonly="readonly"
          class="pb-8 last:pb-0"
          @add="onStepAdd"
          @remove="onStepRemove"
          @change="onStepChange"
        />
      </template>
      <template #secondaryBtn>
        <VBackButton
          v-if="backRoute"
          :label="backRouteLabel"
          :to="backRoute"
        />
      </template>
    </VForm>
  </VLoader>

  <CommissionVersionDescriptionModal v-model:visible="isRevShareDialogOpened" />
</template>
